import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, AlertCircle, CheckCircle, XCircle, Scale, Camera } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_rejuven-health/artifacts/rqq06olv_Rejuven%20Logo%20-%20Website.png';

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-[#FAF8F7] text-[#2B2625]">
      {/* Header */}
      <header className="border-b border-[#E8D5D1]/60 bg-white/70 backdrop-blur-2xl sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center"><img src={LOGO_URL} alt="Rejuven" className="h-11 w-auto" /></Link>
            <Link to="/" className="flex items-center gap-2 text-[#6C6361] hover:text-[#2B2625] text-sm"><ArrowLeft className="w-4 h-4" />Back to Home</Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-[#F3EFEF] flex items-center justify-center"><FileText className="w-6 h-6 text-[#C28B87]" /></div>
          <div>
            <h1 className="font-display text-4xl text-[#2B2625] font-medium">Terms of Service</h1>
            <p className="text-[#6C6361] text-sm">Last updated: December 2025</p>
          </div>
        </div>

        <div className="prose prose-lg max-w-none text-[#6C6361] prose-headings:font-display prose-headings:text-[#2B2625] prose-headings:font-medium prose-strong:text-[#2B2625] prose-a:text-[#C28B87] prose-li:marker:text-[#C28B87]">
          <p className="lead text-lg">Welcome to Rejuven. By accessing or using our precision wellness platform, you agree to these Terms of Service. Please read them carefully.</p>

          <div className="bg-[#FBF3EF] border border-[#E8D5D1] rounded-2xl p-6 my-8 not-prose">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-6 h-6 text-[#C28B87] flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-semibold text-[#2B2625] mb-2">Important Medical Disclaimer</h3>
                <p className="text-sm text-[#6C6361]">Rejuven is an educational, preventative wellness platform. It does not provide medical advice, diagnosis or treatment, and does not prescribe medications, hormones or peptides. Our founder is a medical doctor and researcher but is not a licensed physician in the United States, and no content on the platform constitutes the practice of medicine. Always consult a qualified, licensed healthcare provider for medical decisions.</p>
              </div>
            </div>
          </div>

          <h2>1. Acceptance of Terms</h2>
          <p>By creating an account or using Rejuven&apos;s services, you acknowledge that you have read, understood and agree to be bound by these Terms of Service and our Privacy Policy. If you do not agree, please do not use our services.</p>

          <h2>2. Description of Service</h2>
          <p>Rejuven is a microbiome and genotype-based precision wellness platform for women that provides:</p>
          <ul>
            <li>Educational genotype and microbiome insights (without clinical lab testing).</li>
            <li>An AI wellness companion (&ldquo;Dr. Erika&rdquo;) offering educational, preventative information.</li>
            <li>Personalized nutrition, lifestyle and habit guidance across fertility, general nutrition, early-adult wellness, menopause, longevity and child nutrition.</li>
            <li>Educational information on bioidentical hormones and peptides — for understanding only, not prescription or protocols.</li>
          </ul>

          <h2 className="flex items-center gap-2"><Camera className="w-5 h-5 text-[#899285]" />3. Biometric Analysis Consent</h2>
          <p>Certain features may offer optional camera-based (biometric) analysis to help generate educational genotype insights. This is voluntary. By choosing to use it, you consent to the collection and processing of facial images and derived markers solely for wellness guidance, as described in our Privacy Policy. You may decline or withdraw consent at any time.</p>

          <h2 className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-[#899285]" />4. User Responsibilities</h2>
          <ul>
            <li>Provide accurate, complete information when creating your account.</li>
            <li>Maintain the security of your account credentials.</li>
            <li>Use the service for personal, non-commercial purposes.</li>
            <li>Not share your account with others.</li>
            <li>Comply with all applicable laws and regulations.</li>
            <li>Not attempt to reverse engineer, hack or compromise our systems.</li>
          </ul>

          <h2 className="flex items-center gap-2"><XCircle className="w-5 h-5 text-[#C28B87]" />5. Prohibited Uses</h2>
          <ul>
            <li>Violating any laws or regulations.</li>
            <li>Impersonating another person or entity.</li>
            <li>Uploading malicious content or software.</li>
            <li>Attempting unauthorized access to our systems.</li>
            <li>Using automated systems to scrape or collect data.</li>
            <li>Interfering with other users&apos; access to the service.</li>
          </ul>

          <h2>6. Intellectual Property</h2>
          <p>All content, features and functionality of the Rejuven platform — including text, graphics, logos, images and software — are the exclusive property of Rejuven Inc. and are protected by applicable intellectual property laws.</p>

          <h2>7. Health &amp; Wellness Disclaimer</h2>
          <p>Rejuven is designed to be an educational, preventative wellness companion, not a substitute for professional medical care. Our platform and AI companion:</p>
          <ul>
            <li>Do not diagnose, treat, cure or prevent any disease or medical condition.</li>
            <li>Do not prescribe, dose or recommend medications, hormones or peptides.</li>
            <li>Provide educational information, not the practice of medicine.</li>
            <li>Should not be relied upon in medical emergencies — call your local emergency number.</li>
          </ul>
          <p>Always seek the advice of a licensed physician or qualified healthcare provider regarding any medical condition or before making changes to your health regimen.</p>

          <h2>8. Limitation of Liability</h2>
          <p>To the maximum extent permitted by law, Rejuven Inc. shall not be liable for any indirect, incidental, special, consequential or punitive damages, or any loss of profits, data or goodwill, resulting from your use of, or inability to use, our services.</p>

          <h2>9. Account Termination</h2>
          <p>We reserve the right to suspend or terminate your account if you violate these Terms. You may delete your account at any time through your account settings.</p>

          <h2>10. Changes to Terms</h2>
          <p>We may update these Terms from time to time. Continued use after changes are posted constitutes acceptance of the updated Terms.</p>

          <h2 className="flex items-center gap-2"><Scale className="w-5 h-5 text-[#899285]" />11. Governing Law</h2>
          <p>These Terms are governed by the laws of the State of Delaware, United States, without regard to its conflict of law provisions.</p>

          <div className="bg-[#F3EFEF] rounded-2xl p-6 my-8 not-prose">
            <h3 className="text-[#2B2625] font-semibold mb-2">Contact Us</h3>
            <p className="text-sm text-[#6C6361]">Questions about these Terms? Email us at <a href="mailto:legal@rejuvenhealth.com" className="text-[#C28B87] font-medium">legal@rejuvenhealth.com</a>.</p>
          </div>
        </div>
      </main>

      <footer className="border-t border-[#E8D5D1]/60 py-8 mt-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-sm text-[#6C6361]">© 2026 Rejuven Inc. All rights reserved.</div>
      </footer>
    </div>
  );
}
